import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Stack,
  Divider,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Email,
  Phone,
  Business,
  Lock,
  CheckCircle,
  Info as InfoIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
} from "@mui/icons-material";
import * as myApi from "../../components/Api";
import {
  validateEmail,
  validatePhone,
  validateOrganization,
  validateText,
} from "../../utils/formValidation";
import Logo from "../../components/Logo";
import AuthBackgroundSlideshow from "../../components/AuthBackground/Slideshow";

/**
 * Modern Sign Up Page with Material UI
 * Features:
 * - 2-column responsive grid layout
 * - Real-time validation feedback
 * - Password strength indicator
 * - Terms agreement checkbox
 * - Clear requirements list
 */
export default function SignUp() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    email: "",
    phone: "",
    organization: "",
    name: "",
    password: "",
    confirmPassword: "",
  });
  const [inactiveDialogOpen, setInactiveDialogOpen] = useState(false);
  const [inactiveEmail, setInactiveEmail] = useState("");

  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    number: false,
    special: false,
  });

  useEffect(() => {
    paperSubmissionExpiration();
  }, []);

  const paperSubmissionExpiration = async () => {
    try {
      const response = await myApi.get("/deadline");
      if (response.data.result) {
        const deadlineInfo = response.data.result;
        const dateNow = new Date();
        const dateLimit = new Date(deadlineInfo.general_deadline);

        if (dateNow > dateLimit) {
          toast("Submission is closed", { type: "error" });
          setTimeout(() => {
            window.location.href = "/auth/sign-in/";
          }, 3000);
        }
      }
    } catch (error) {
      console.error("Error fetching deadline:", error);
      toast("Error checking submission deadline", { type: "error" });
    }
  };

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  const handlePasswordChange = (e) => {
    checkPasswordStrength(e.target.value);
    if (validationErrors.password) {
      const validation = validatePassword(e.target.value);
      setValidationErrors((prev) => ({
        ...prev,
        password: validation.valid ? "" : validation.message,
      }));
    }
  };

  const validatePassword = (password) => {
    if (!password || password.trim() === "") {
      return { valid: false, message: "Password is required" };
    }
    if (password.length < 8) {
      return { valid: false, message: "Password must be at least 8 characters" };
    }
    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      return { valid: false, message: "Password must contain at least 1 uppercase letter" };
    }
    // Check for number
    if (!/[0-9]/.test(password)) {
      return { valid: false, message: "Password must contain at least 1 number" };
    }
    // Check for special character
    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;
    if (!specialCharRegex.test(password)) {
      return { valid: false, message: "Password must contain at least 1 special character (!@#$%^&*...)" };
    }
    return { valid: true, message: "" };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    const formData = new FormData(event.currentTarget);

    // Validate all fields
    const errors = {};

    const nameValidation = validateText(formData.get("name"), {
      minLength: 3,
      maxLength: 100,
      required: true,
      fieldName: "Full Name",
    });
    if (!nameValidation.valid) errors.name = nameValidation.message;

    const emailValidation = validateEmail(formData.get("email"));
    if (!emailValidation.valid) errors.email = emailValidation.message;

    const orgValidation = validateOrganization(formData.get("organization"));
    if (!orgValidation.valid) errors.organization = orgValidation.message;

    const phoneValidation = validatePhone(formData.get("handphone"));
    if (!phoneValidation.valid) errors.phone = phoneValidation.message;

    const passwordValidation = validatePassword(formData.get("password"));
    if (!passwordValidation.valid) errors.password = passwordValidation.message;

    const confirmPassword = formData.get("confirmPassword");
    if (!confirmPassword || confirmPassword.trim() === "") {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.get("password") !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!agreedToTerms) {
      toast("You must agree to the terms and conditions", { type: "error" });
      setLoading(false);
      return;
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast("Please fix the validation errors", { type: "error" });
      setLoading(false);
      return;
    }

    setValidationErrors({});

    const data = {
      data_nama: formData.get("name"),
      data_email: formData.get("email"),
      data_handphone: formData.get("handphone"),
      data_organisasi: formData.get("organization"),
      data_password: formData.get("password"),
    };

    try {
      const response = await myApi.post("/register", data);

      if (response.data && response.data.status === "success") {
        toast.success("Registration successful! Please check your email for activation code.", {
          position: "top-right",
          autoClose: 5000,
        });
        // Navigate to activation page with email in URL
        setTimeout(() => {
          navigate(`/auth/activation/?email=${encodeURIComponent(formData.get("email"))}`);
        }, 2000);
      } else if (response.data?.status === "inactive") {
        // Handle inactive user case
        toast.warning("Account already registered but not activated. Please activate your account.", {
          position: "top-right",
          autoClose: 5000,
        });
        setInactiveEmail(formData.get("email"));
        setInactiveDialogOpen(true);
      } else {
        toast.error(response.data?.message || "Registration failed. Please try again.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Registration failed. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordScore = Object.values(passwordStrength).filter(Boolean).length;

  return (
    <>
      {/* Animated Background Slideshow */}
      <AuthBackgroundSlideshow />
      <Container maxWidth="md" sx={{ minHeight: "100vh", py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: "center" }}>
          <Logo size={100} type="horizontal" />
        </Box>

        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Create Account
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Join KARSA NUSA and submit your paper
        </Typography>

        {/* Registration Form */}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Full Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Full Name"
                name="name"
                placeholder="John Doe"
                error={!!validationErrors.name}
                helperText={validationErrors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonAddIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Organization */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Organization"
                name="organization"
                placeholder="University or Institution"
                error={!!validationErrors.organization}
                helperText={validationErrors.organization}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Email Address"
                name="email"
                type="email"
                placeholder="email@example.com"
                error={!!validationErrors.email}
                helperText={validationErrors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                name="handphone"
                type="tel"
                placeholder="+62 812-3456-7890"
                error={!!validationErrors.phone}
                helperText={validationErrors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Password"
                name="password"
                type="password"
                placeholder="Min 8 characters"
                error={!!validationErrors.password}
                helperText={validationErrors.password}
                onChange={handlePasswordChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Confirm Password */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                placeholder="Re-enter password"
                error={!!validationErrors.confirmPassword}
                helperText={validationErrors.confirmPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Password Strength Indicator */}
            <Grid item xs={12}>
              <Box sx={{ mt: -2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Password Requirements:
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Typography
                    variant="caption"
                    color={passwordStrength.length ? "success.main" : "text.secondary"}
                  >
                    {passwordStrength.length ? "✓" : "○"} Min 8 characters
                  </Typography>
                  <Typography
                    variant="caption"
                    color={passwordStrength.uppercase ? "success.main" : "text.secondary"}
                  >
                    {passwordStrength.uppercase ? "✓" : "○"} 1 uppercase letter
                  </Typography>
                  <Typography
                    variant="caption"
                    color={passwordStrength.number ? "success.main" : "text.secondary"}
                  >
                    {passwordStrength.number ? "✓" : "○"} 1 number
                  </Typography>
                  <Typography
                    variant="caption"
                    color={passwordStrength.special ? "success.main" : "text.secondary"}
                  >
                    {passwordStrength.special ? "✓" : "○"} 1 special character
                  </Typography>
                </Stack>
              </Box>
            </Grid>

            {/* Terms Agreement */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    color="primary"
                    required
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{" "}
                    <Link
                      to="/term-conditions/"
                      style={{ fontWeight: 600, textDecoration: "none" }}
                    >
                      Terms & Conditions
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy-policy/"
                      style={{ fontWeight: 600, textDecoration: "none" }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading || !agreedToTerms}
                startIcon={<PersonAddIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  mt: 1,
                }}
              >
                {loading ? "Creating Account..." : "Create Account"}
              </Button>
            </Grid>

            {/* Sign In Link */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>
              <Typography variant="body2" textAlign="center">
                Already have an account?{" "}
                <Link
                  to="/auth/sign-in/"
                  style={{ fontWeight: 600, textDecoration: "none" }}
                >
                  Sign In
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </form>

        {/* Footer */}
        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: "divider",
            textAlign: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            © 2026 KARSA NUSA. All rights reserved.
          </Typography>
        </Box>
      </Paper>

      {/* Dialog for inactive users */}
      <Dialog
        open={inactiveDialogOpen}
        onClose={() => setInactiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <MarkEmailUnreadIcon color="warning" sx={{ fontSize: 32 }} />
            <Typography variant="h6" component="span">
              Account Already Exists
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            An account with the email <strong>{inactiveEmail}</strong> already exists but is not activated.
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              To activate your account, please login first and then request a new activation code from the activation page.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setInactiveDialogOpen(false)}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() => navigate("/auth/sign-in/")}
            variant="contained"
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </>
  );
}
